/*
 * Event concept signup form (sections/event-concept.liquid).
 * Subscribes to Klaviyo with the client-side API (public key only, no backend):
 *   1. Tour list: email consent + per-city profile properties + UTMs (must succeed)
 *   2. Main list (optional), SMS list (if opted in), and a signup event, in parallel
 * Self-contained on purpose: no dependency on the theme's Alpine build, so it survives theme updates.
 */
(() => {
  if (customElements.get('concept-signup-form')) return;

  const KLAVIYO_REVISION = '2026-10-15';
  const ATTRIBUTION_KEY = 'concept_attribution';
  const SUBMIT_COUNT_KEY = 'concept_submit_count';
  const MAX_SUBMITS_PER_SESSION = 3;
  const MIN_FILL_MS = 2500;
  const ATTRIBUTION_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid'];
  const DISPOSABLE_DOMAINS = [
    'mailinator.com', 'guerrillamail.com', '10minutemail.com', 'tempmail.com', 'temp-mail.org',
    'yopmail.com', 'trashmail.com', 'sharklasers.com', 'getnada.com', 'dispostable.com',
    'maildrop.cc', 'throwawaymail.com', 'fakeinbox.com', 'mailnesia.com', 'mintemail.com'
  ];

  const storage = {
    get(key) {
      try { return JSON.parse(sessionStorage.getItem(key)); } catch (e) { return null; }
    },
    set(key, value) {
      try { sessionStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* private mode */ }
    }
  };

  // Keep the visit's UTMs even if the visitor browses other pages before signing up.
  // A new tagged landing replaces the stored values; an untagged page view never does.
  const captureAttribution = () => {
    const params = new URLSearchParams(window.location.search);
    const found = {};
    ATTRIBUTION_PARAMS.forEach((key) => {
      const value = params.get(key);
      if (value) found[key] = value.slice(0, 200);
    });
    const stored = storage.get(ATTRIBUTION_KEY);
    if (Object.keys(found).length || !stored) {
      storage.set(ATTRIBUTION_KEY, {
        ...found,
        referrer: document.referrer || '',
        landing_url: window.location.href.slice(0, 500)
      });
    }
    return storage.get(ATTRIBUTION_KEY) || {};
  };

  const toE164 = (raw) => {
    const digits = (raw || '').replace(/\D/g, '');
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
    return null;
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);

  const klaviyoPost = (path, companyId, body) =>
    fetch(`https://a.klaviyo.com/client/${path}?company_id=${encodeURIComponent(companyId)}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        accept: 'application/vnd.api+json',
        revision: KLAVIYO_REVISION
      },
      body: JSON.stringify({ data: body })
    }).then((response) => {
      if (!response.ok) throw new Error(`Klaviyo ${path} ${response.status}`);
      return response;
    });

  const subscription = (listId, attributes, source) => ({
    type: 'subscription',
    attributes: {
      custom_source: source,
      profile: { data: { type: 'profile', attributes } }
    },
    relationships: { list: { data: { type: 'list', id: listId } } }
  });

  class ConceptSignupForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-concept-form]');
      if (!this.form) return;

      this.renderedAt = Date.now();
      this.submitting = false;
      this.attribution = captureAttribution();
      this.errorEl = this.querySelector('[data-concept-error]');
      this.smsToggle = this.querySelector('[data-concept-sms-toggle]');
      this.smsDetails = this.querySelector('[data-concept-sms-details]');

      this.form.addEventListener('submit', (event) => this.onSubmit(event));
      this.smsToggle?.addEventListener('change', () => {
        this.smsDetails.hidden = !this.smsToggle.checked;
        if (this.smsToggle.checked) this.form.elements.phone?.focus();
      });

      const city = new URLSearchParams(window.location.search).get('city');
      if (city) this.selectCity(city);
    }

    selectCity(key) {
      const input = Array.from(this.form.querySelectorAll('input[name="city"]'))
        .find((el) => el.value === key.toLowerCase());
      if (input) input.checked = true;
    }

    showError(message, field) {
      this.form.querySelectorAll('[aria-invalid]').forEach((el) => el.removeAttribute('aria-invalid'));
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
      if (field) {
        field.setAttribute('aria-invalid', 'true');
        field.focus();
      }
    }

    clearError() {
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
      this.form.querySelectorAll('[aria-invalid]').forEach((el) => el.removeAttribute('aria-invalid'));
    }

    showSuccess() {
      this.form.hidden = true;
      const intro = this.querySelector('[data-concept-intro]');
      if (intro) intro.hidden = true;
      const success = this.querySelector('[data-concept-success]');
      success.hidden = false;
      success.querySelector('[data-concept-success-heading]')?.focus();
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this.submitting) return;
      this.clearError();

      const els = this.form.elements;
      const { companyId, listId, mainListId, smsListId, prefix, eventName, source } = this.dataset;

      // Bots: the honeypot is invisible to people, and humans can't finish this fast.
      // Pretend it worked so the bot doesn't retry.
      if (els.company?.value) return this.showSuccess();
      if (Date.now() - this.renderedAt < MIN_FILL_MS) {
        return this.showError('Hang on a sec, then try again.');
      }

      const email = els.email.value.trim().toLowerCase();
      if (!isValidEmail(email)) return this.showError('Please enter a valid email address.', els.email);
      if (DISPOSABLE_DOMAINS.includes(email.split('@')[1])) {
        return this.showError('Please use your everyday email so you don\'t miss ticket news.', els.email);
      }

      const firstName = (els.first_name?.value || '').trim();
      const lastName = (els.last_name?.value || '').trim();
      if (els.first_name && !firstName) return this.showError('Please enter your first name.', els.first_name);
      if (els.last_name && !lastName) return this.showError('Please enter your last name.', els.last_name);

      const cityInputs = Array.from(this.form.querySelectorAll('input[name="city"]:checked'));
      const otherCity = (els.other_city?.value || '').trim();
      if (!cityInputs.length) {
        const first = this.form.querySelector('input[name="city"]');
        return this.showError('Pick at least one city.', first);
      }

      const smsOptIn = Boolean(this.smsToggle?.checked);
      const phone = smsOptIn ? toE164(els.phone.value) : null;
      if (smsOptIn && !phone) {
        return this.showError('Please enter a 10-digit US mobile number, or uncheck the text option.', els.phone);
      }

      if (!companyId || !listId) {
        console.error('[concept-signup-form] Missing Klaviyo company ID or list ID in section settings.');
        return this.showError('Sorry, signup isn\'t available right now. Please try again later.');
      }

      const submits = storage.get(SUBMIT_COUNT_KEY) || 0;
      if (submits >= MAX_SUBMITS_PER_SESSION) return this.showSuccess();

      // Only set true for picked cities; never send false, so earlier picks are kept.
      const properties = { [`${prefix}_signup_at`]: new Date().toISOString() };
      cityInputs.forEach((input) => { properties[`${prefix}_city_${input.value}`] = true; });
      if (otherCity) properties[`${prefix}_city_other`] = otherCity.slice(0, 100);
      ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach((key) => {
        if (this.attribution[key]) properties[`${prefix}_last_${key}`] = this.attribution[key];
      });

      const profile = { email, properties };
      if (firstName) profile.first_name = firstName.slice(0, 100);
      if (lastName) profile.last_name = lastName.slice(0, 100);

      const emailConsent = { email: { marketing: { consent: 'SUBSCRIBED' } } };
      const smsConsent = { sms: { marketing: { consent: 'SUBSCRIBED' } } };
      const tourSubscriptions = { ...emailConsent };
      if (phone && !smsListId) {
        profile.phone_number = phone;
        Object.assign(tourSubscriptions, smsConsent);
      }
      profile.subscriptions = tourSubscriptions;

      const submitButton = this.form.querySelector('[type="submit"]');
      this.submitting = true;
      submitButton.disabled = true;
      submitButton.setAttribute('aria-busy', 'true');

      try {
        await klaviyoPost('subscriptions', companyId, subscription(listId, profile, source));
      } catch (error) {
        console.error('[concept-signup-form]', error);
        this.submitting = false;
        submitButton.disabled = false;
        submitButton.removeAttribute('aria-busy');
        return this.showError('Something went wrong on our end. Please try again in a moment.');
      }

      const followUps = [];
      if (mainListId && mainListId !== listId) {
        followUps.push(klaviyoPost('subscriptions', companyId,
          subscription(mainListId, { email, subscriptions: emailConsent }, source)));
      }
      if (phone && smsListId) {
        followUps.push(klaviyoPost('subscriptions', companyId,
          subscription(smsListId, { email, phone_number: phone, subscriptions: smsConsent }, source)));
      }
      if (eventName) {
        followUps.push(klaviyoPost('events', companyId, {
          type: 'event',
          attributes: {
            metric: { data: { type: 'metric', attributes: { name: eventName } } },
            profile: { data: { type: 'profile', attributes: { email } } },
            properties: {
              cities: cityInputs.map((input) => input.dataset.label),
              city_keys: cityInputs.map((input) => input.value),
              other_city: otherCity,
              sms_opt_in: Boolean(phone),
              ...this.attribution,
              page_url: window.location.href.slice(0, 500)
            }
          }
        }));
      }
      const results = await Promise.allSettled(followUps);
      results.filter((r) => r.status === 'rejected').forEach((r) => console.warn('[concept-signup-form]', r.reason));

      storage.set(SUBMIT_COUNT_KEY, submits + 1);

      // Cookie the visitor in Klaviyo's onsite JS so later ticket clicks are attributed.
      window.klaviyo?.push?.(['identify', { email }]);
      window.Shopify?.analytics?.publish?.('concept_signup', { cities: cityInputs.map((i) => i.value) });
      this.dispatchEvent(new CustomEvent('concept:subscribed', { bubbles: true }));

      this.showSuccess();
    }
  }

  customElements.define('concept-signup-form', ConceptSignupForm);

  // Mobile sticky button: shows once the hero has scrolled away, hides while the form is on screen.
  const initSticky = () => {
    const sticky = document.querySelector('[data-concept-sticky]');
    const signup = document.querySelector('[data-concept-signup]');
    if (!sticky || !signup || !('IntersectionObserver' in window)) return;

    const hero = document.querySelector('[data-concept-hero]');
    const inView = new Map();
    const update = () => {
      const show = !inView.get(hero) && !inView.get(signup);
      sticky.classList.toggle('is-visible', show);
      document.body.classList.toggle('concept-sticky-on', show);
    };
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => inView.set(entry.target, entry.isIntersecting));
      update();
    });
    [hero, signup].filter(Boolean).forEach((el) => observer.observe(el));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSticky);
  } else {
    initSticky();
  }

  // "Get notified" buttons and hero city tiles pre-tick their city in the form.
  // Ticket links are tracked in Klaviyo for visitors it already knows.
  document.addEventListener('click', (event) => {
    const cityLink = event.target.closest('[data-concept-city]');
    if (cityLink) {
      const target = document.querySelector(cityLink.getAttribute('href'));
      target?.querySelector('concept-signup-form')?.selectCity(cityLink.dataset.conceptCity);
      return;
    }

    const ticketLink = event.target.closest('[data-concept-ticket]');
    if (ticketLink) {
      const name = ticketLink.closest('[data-concept-ticket-event]')?.dataset.conceptTicketEvent;
      if (name) {
        window.klaviyo?.push?.(['track', name, {
          city_key: ticketLink.dataset.conceptTicket,
          url: ticketLink.href
        }]);
      }
    }
  });
})();
